/**
 * @fileoverview Authentication Context Provider
 * @module AuthContext
 * 
 * Business Context:
 * - Manages user authentication state across the application
 * - Handles JWT token storage and refresh logic
 * - Provides authentication methods for login, register, logout
 * 
 * Technical Context:
 * - Uses localStorage for token persistence
 * - Integrates with backend authentication API
 * - Provides React context for global auth state management
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../api/client';

interface Property {
  id: string;
  propertyType: 'single_family' | 'condo' | 'townhouse' | 'mobile_home';
  addressLine1: string;
  addressLine2?: string;
  city: string;
  zipCode: string;
  countyId: number;
  county: {
    id: number;
    name: string;
    state: string;
  };
  homesteadStatus: boolean;
  yearBuilt?: number;
  squareFootage?: number;
  createdAt: string;
}

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  tier: 'standard' | 'ai_enhanced';
  createdViaPurchase?: boolean;
  createdAt: string;
  properties?: Property[];
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  setUserAndTokens: (user: User, accessToken: string) => Promise<void>;
  getUserPersonalizationContext: () => { propertyType?: 'single_family' | 'condo' | 'townhouse' | 'mobile_home' | undefined; countyId?: number | undefined; tier: 'standard' | 'ai_enhanced'; };
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing token on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('tbyt_token');
      if (token) {
        // Verify token and get user info
        await fetchUserProfile(token);
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const fetchUserProfile = async (token: string): Promise<void> => {
    try {
      // Only set the token if it's different from current
      const currentToken = localStorage.getItem('tbyt_token');
      if (currentToken !== token) {
        localStorage.setItem('tbyt_token', token);
      }
      
      const response = await apiClient.users.getProfile();

      // If successful, the token is valid - set user with full profile including properties
      setUser(response.data.data.profile || response.data.data);

    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      
      // Token is invalid - remove it and clear user
      localStorage.removeItem('tbyt_token');
      setUser(null);
      // Don't re-throw - error is handled here
    }
  };

  const login = async (email: string, password: string, rememberMe: boolean = false): Promise<void> => {
    try {
      const response = await apiClient.auth.login({ email, password, rememberMe });

      // Store both access and refresh tokens
      const { accessToken, refreshToken: refreshTkn } = response.data.data.tokens;
      localStorage.setItem('tbyt_token', accessToken);
      if (refreshTkn) {
        localStorage.setItem('tbyt_refresh_token', refreshTkn);
      }
      
      // Fetch full profile including properties
      // The login response only includes basic user data without properties
      await fetchUserProfile(accessToken);

    } catch (error) {
      throw error;
    }
  };

  const register = async (
    email: string, 
    password: string, 
    firstName?: string, 
    lastName?: string
  ): Promise<void> => {
    try {
      const response = await apiClient.auth.register({ email, password, firstName, lastName });

      // Store both access and refresh tokens
      const accessToken = response.data.data.accessToken;
      const refreshTkn = response.data.data.refreshToken;
      localStorage.setItem('tbyt_token', accessToken);
      if (refreshTkn) {
        localStorage.setItem('tbyt_refresh_token', refreshTkn);
      }
      
      // Fetch full profile including properties
      await fetchUserProfile(accessToken);
    } catch (error) {
      throw error;
    }
  };

  const logout = (): void => {
    localStorage.removeItem('tbyt_token');
    localStorage.removeItem('tbyt_refresh_token');
    setUser(null);
  };

  const refreshToken = async (): Promise<void> => {
    const refreshTkn = localStorage.getItem('tbyt_refresh_token');
    
    if (!refreshTkn) {
      // No refresh token available, user needs to log in again
      logout();
      return;
    }

    try {
      const response = await apiClient.auth.refresh({ refreshToken: refreshTkn });
      
      if (response.data?.success && response.data?.data?.tokens) {
        const { accessToken, refreshToken: newRefreshToken } = response.data.data.tokens;
        
        // Store new tokens
        localStorage.setItem('tbyt_token', accessToken);
        if (newRefreshToken) {
          localStorage.setItem('tbyt_refresh_token', newRefreshToken);
        }
        
        // Fetch updated user profile
        await fetchUserProfile(accessToken);
      } else {
        throw new Error('Invalid refresh response');
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Refresh failed, clear tokens and user state
      logout();
    }
  };

  const setUserAndTokens = async (userData: User, accessToken: string): Promise<void> => {
    localStorage.setItem('tbyt_token', accessToken);
    // Fetch full profile including properties instead of using partial user data
    await fetchUserProfile(accessToken);
  };

  // Get user's personalization context for course API calls
  const getUserPersonalizationContext = (): { propertyType?: 'single_family' | 'condo' | 'townhouse' | 'mobile_home' | undefined; countyId?: number | undefined; tier: 'standard' | 'ai_enhanced'; } => {
    const primaryProperty = user?.properties?.[0]; // Use first property as primary
    return {
      propertyType: primaryProperty?.propertyType,
      countyId: primaryProperty?.countyId,
      tier: (user?.tier || 'standard') as 'standard' | 'ai_enhanced'
    };
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshToken,
    setUserAndTokens,
    getUserPersonalizationContext,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 